/**
 * Indicadores: os números de topo, na ordem e no estilo que o usuário escolheu.
 *
 * O `*` ao lado de uma taxa não é enfeite: com mais de um projeto no filtro a API recalcula as
 * taxas a partir dos totais somados, e a diferença entre um número e uma estimativa é o que a
 * marca preserva.
 *
 * O desenho (os quatro estilos) está em `KpiRow`, que recebe o número já escrito — é o que deixa a
 * mesma faixa servir às telas das 32 métricas e às que têm números próprios, como Cliques, sem uma
 * segunda cópia dos quatro layouts.
 */
import { Settings2 } from 'lucide-react';
import { GhostBtn } from '@/components/v2/ui';
import { c, v } from '@/components/v2/theme';
import type { Metrics } from '@/types/email-v1';
import { M, readMetric } from '../../lib/metrics';
import type { Fmtr } from '../../lib/format';
import { KPI_CHOICES, type KpiStyle } from '../../lib/prefs';

/** Um indicador pronto para desenhar: o rótulo, o número já escrito e o que ele quer dizer. */
export interface KpiItem {
    k: string;
    l: string;
    /** O denominador, quando o rótulo sozinho não diz de quê: "dos entregues". */
    den?: string;
    d: string;
    text: string;
    /** A marca `*` de recalculada a partir de totais somados. */
    mark?: boolean;
    color?: string;
}

export function Kpis({ totals, keys, style, derived, fmt, onCustomize, sub, markTitle }: {
    totals: Metrics;
    keys: string[];
    style: KpiStyle;
    derived: boolean;
    fmt: Fmtr;
    onCustomize: () => void;
    /** O que estes números somam. Na Geral é o período; numa lista, as linhas dela. */
    sub?: string;
    /** O que a marca `*` quer dizer aqui — nas listas, "somados desta lista". */
    markTitle?: string;
}) {
    const items: KpiItem[] = keys.filter((k) => M[k]).map((k) => {
        const met = M[k];
        const value = readMetric(totals, k);
        return {
            k, l: met.l, den: met.den, d: met.d,
            text: fmt.metric(met, value),
            mark: met.block === 'rates' && value != null && derived,
            color: met.hue ? c(met.hue) : undefined,
        };
    });
    return (
        <KpiRow items={items} style={style} onCustomize={onCustomize}
            sub={sub ?? `${items.length} de ${KPI_CHOICES.length}`} markTitle={markTitle} />
    );
}

/**
 * A faixa de indicadores, nos quatro estilos. Nada aqui sabe de onde veio o número: quem lê a
 * linha e formata o valor é quem chamou.
 */
export function KpiRow({ items, style, sub, markTitle, onCustomize, action = 'Escolher indicadores' }: {
    items: KpiItem[];
    style: KpiStyle;
    sub: string;
    markTitle?: string;
    onCustomize: () => void;
    /** O texto do botão de ajuste — muda quando o que se ajusta não é a escolha das métricas. */
    action?: string;
}) {
    if (!items.length) return null;

    const head = (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Indicadores</h2>
            <span style={{ fontSize: 11.5, color: v('faint') }}>{sub}</span>
            <div style={{ marginLeft: 'auto' }}>
                <GhostBtn onClick={onCustomize}><Settings2 size={14} color={v('muted')} />{action}</GhostBtn>
            </div>
        </div>
    );

    const mark = (on: boolean) => (on
        ? <abbr title={markTitle ?? 'Recalculada a partir dos totais somados dos projetos'} style={{ textDecoration: 'none', color: v('faint') }}>*</abbr>
        : null);

    if (style === 'list') {
        return (
            <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {head}
                <div style={{ background: v('surface'), border: `1px solid ${v('border')}`, borderRadius: 18, overflow: 'hidden' }}>
                    {items.map((x, i) => (
                        <div key={x.k} title={x.d}
                            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 18px', borderTop: i ? `1px solid ${v('border-soft')}` : 0 }}>
                            <span style={{ fontSize: 13, color: v('text-2'), flex: 1, minWidth: 0 }}>
                                {x.l}{x.den && <span style={{ color: v('faint') }}> {x.den}</span>}
                            </span>
                            <span style={{ fontSize: 15, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: x.color }}>
                                {x.text}{mark(!!x.mark)}
                            </span>
                        </div>
                    ))}
                </div>
            </section>
        );
    }

    const hero = style === 'hero';
    const compact = style === 'grid';
    const min = compact ? 150 : hero ? 260 : 200;

    return (
        <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {head}
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit,minmax(min(100%,${min}px),1fr))`, gap: 12 }}>
                {items.map((x, i) => {
                    const big = hero && i === 0;
                    return (
                        <div key={x.k} title={x.d}
                            style={{
                                background: v('surface'), border: `1px solid ${v('border')}`, borderRadius: 14,
                                padding: compact ? '11px 13px' : '14px 16px',
                                display: 'flex', flexDirection: 'column', gap: 5,
                                gridColumn: big ? 'span 2' : undefined,
                            }}>
                            <span style={{ fontSize: 11.5, color: v('faint') }}>
                                {x.l}{x.den && <span> {x.den}</span>}
                            </span>
                            <span style={{
                                fontSize: big ? 34 : compact ? 20 : 26, fontWeight: 600, letterSpacing: '-0.02em',
                                fontVariantNumeric: 'tabular-nums', color: x.color, whiteSpace: 'nowrap',
                            }}>
                                {x.text}{mark(!!x.mark)}
                            </span>
                            {!compact && <span style={{ fontSize: 11.5, color: v('muted'), lineHeight: 1.4 }}>{x.d}</span>}
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
